/**
 * Mockup Library - Sammlung von SVG-basierten Geräte-Mockups
 *
 * Kategorien:
 * - phone: Smartphones (iPhone, Android)
 * - tablet: Tablets (iPad)
 * - laptop: Laptops (MacBook)
 * - desktop: Desktop-Monitore
 * - watch: Smartwatches
 * - apparel: Kleidung und Merchandise (T-Shirt, Tasse, etc.)
 *
 * Jedes Mockup definiert einen screenArea-Bereich in Prozent,
 * wo das Benutzer-Content platziert wird.
 *
 * Alle Mockups verwenden viewBox="0 0 100 100" für Konsistenz.
 */

import type { MockupElement } from './elementTypes';

// ============================================================================
// PHONE MOCKUPS
// ============================================================================

const iphone15Front: MockupElement = {
  id: 'mockup-iphone15-front',
  type: 'mockup',
  name: 'iPhone 15 Frontal',
  tags: ['iphone', 'apple', 'smartphone', 'front', 'ios'],
  category: 'phone',
  deviceType: 'phone',
  screenArea: { x: 8, y: 4, width: 84, height: 92, borderRadius: 8 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Geräte-Rahmen -->
    <rect x="2" y="0" width="96" height="100" rx="12" ry="12" fill="#1a1a1a"/>
    <!-- Innerer Rahmen (Titanium-Effekt) -->
    <rect x="4" y="1" width="92" height="98" rx="11" ry="11" fill="#2d2d2d"/>
    <!-- Bildschirm-Bereich -->
    <rect x="8" y="4" width="84" height="92" rx="8" ry="8" fill="#e5e7eb"/>
    <!-- Dynamic Island -->
    <rect x="35" y="6" width="30" height="6" rx="3" ry="3" fill="#1a1a1a"/>
    <!-- Unterer Home-Indikator -->
    <rect x="38" y="93" width="24" height="2" rx="1" ry="1" fill="rgba(0,0,0,0.3)"/>
  </svg>`
};

const iphone15Angled: MockupElement = {
  id: 'mockup-iphone15-angled',
  type: 'mockup',
  name: 'iPhone 15 Perspektive',
  tags: ['iphone', 'apple', 'smartphone', 'angled', '3d', 'perspective'],
  category: 'phone',
  deviceType: 'phone',
  screenArea: { x: 18, y: 8, width: 68, height: 80, borderRadius: 6 },
  perspective: { rotateX: 5, rotateY: -15 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <!-- Schatten-Gradient -->
      <linearGradient id="iphone-shadow" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="rgba(0,0,0,0.1)"/>
        <stop offset="100%" stop-color="rgba(0,0,0,0.3)"/>
      </linearGradient>
    </defs>
    <!-- Schatten -->
    <ellipse cx="55" cy="96" rx="35" ry="3" fill="rgba(0,0,0,0.15)"/>
    <!-- Geräte-Rahmen (perspektivisch verzerrt) -->
    <path d="M12,5 L95,10 L92,90 L8,95 Z" fill="#1a1a1a" stroke="#3d3d3d" stroke-width="1"/>
    <!-- Innerer Rahmen -->
    <path d="M15,8 L92,13 L89,87 L11,92 Z" fill="#2d2d2d"/>
    <!-- Bildschirm -->
    <path d="M18,11 L88,15 L86,85 L14,90 Z" fill="#e5e7eb"/>
    <!-- Dynamic Island -->
    <path d="M42,13 L62,14 L61,17 L41,16 Z" fill="#1a1a1a" rx="2"/>
    <!-- Seitliche Buttons -->
    <rect x="8" y="25" width="2" height="10" rx="1" fill="#3d3d3d"/>
    <rect x="8" y="40" width="2" height="15" rx="1" fill="#3d3d3d"/>
    <rect x="8" y="58" width="2" height="15" rx="1" fill="#3d3d3d"/>
  </svg>`
};

const androidPhone: MockupElement = {
  id: 'mockup-android-phone',
  type: 'mockup',
  name: 'Android Smartphone',
  tags: ['android', 'smartphone', 'google', 'pixel', 'samsung'],
  category: 'phone',
  deviceType: 'phone',
  screenArea: { x: 6, y: 3, width: 88, height: 94, borderRadius: 6 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Geräte-Rahmen -->
    <rect x="1" y="0" width="98" height="100" rx="10" ry="10" fill="#222"/>
    <!-- Innerer Rahmen -->
    <rect x="3" y="1" width="94" height="98" rx="9" ry="9" fill="#333"/>
    <!-- Bildschirm -->
    <rect x="6" y="3" width="88" height="94" rx="6" ry="6" fill="#e5e7eb"/>
    <!-- Kamera (Punch-Hole) -->
    <circle cx="50" cy="7" r="2.5" fill="#222"/>
    <!-- Status-Leiste Platzhalter -->
    <rect x="10" y="4" width="15" height="2" rx="1" fill="rgba(0,0,0,0.1)"/>
    <rect x="75" y="4" width="15" height="2" rx="1" fill="rgba(0,0,0,0.1)"/>
    <!-- Navigationsleiste unten -->
    <rect x="30" y="94" width="40" height="2" rx="1" fill="rgba(0,0,0,0.2)"/>
  </svg>`
};

// ============================================================================
// TABLET MOCKUPS
// ============================================================================

const ipadPro: MockupElement = {
  id: 'mockup-ipad-pro',
  type: 'mockup',
  name: 'iPad Pro',
  tags: ['ipad', 'apple', 'tablet', 'pro', 'ios'],
  category: 'tablet',
  deviceType: 'tablet',
  screenArea: { x: 5, y: 5, width: 90, height: 90, borderRadius: 4 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Geräte-Rahmen -->
    <rect x="0" y="0" width="100" height="100" rx="6" ry="6" fill="#1a1a1a"/>
    <!-- Innerer Rahmen -->
    <rect x="2" y="2" width="96" height="96" rx="5" ry="5" fill="#2a2a2a"/>
    <!-- Bildschirm -->
    <rect x="5" y="5" width="90" height="90" rx="4" ry="4" fill="#e5e7eb"/>
    <!-- Face ID Kamera (oben Mitte) -->
    <circle cx="50" cy="2.5" r="1" fill="#3a3a3a"/>
    <!-- Magnetischer Apple Pencil Bereich (rechts) -->
    <rect x="98" y="30" width="1.5" height="40" rx="0.5" fill="#4a4a4a"/>
  </svg>`
};

const ipadLandscape: MockupElement = {
  id: 'mockup-ipad-landscape',
  type: 'mockup',
  name: 'iPad Querformat',
  tags: ['ipad', 'apple', 'tablet', 'landscape', 'horizontal'],
  category: 'tablet',
  deviceType: 'tablet',
  screenArea: { x: 4, y: 6, width: 92, height: 88, borderRadius: 3 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Geräte-Rahmen (Querformat) -->
    <rect x="0" y="3" width="100" height="94" rx="5" ry="5" fill="#1a1a1a"/>
    <!-- Innerer Rahmen -->
    <rect x="2" y="4" width="96" height="92" rx="4" ry="4" fill="#2a2a2a"/>
    <!-- Bildschirm -->
    <rect x="4" y="6" width="92" height="88" rx="3" ry="3" fill="#e5e7eb"/>
    <!-- Kamera (links Mitte) -->
    <circle cx="2" cy="50" r="1" fill="#3a3a3a"/>
  </svg>`
};

// ============================================================================
// LAPTOP MOCKUPS
// ============================================================================

const macbookPro: MockupElement = {
  id: 'mockup-macbook-pro',
  type: 'mockup',
  name: 'MacBook Pro',
  tags: ['macbook', 'apple', 'laptop', 'notebook', 'macos'],
  category: 'laptop',
  deviceType: 'laptop',
  screenArea: { x: 12, y: 5, width: 76, height: 50, borderRadius: 2 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Bildschirm-Deckel -->
    <rect x="8" y="2" width="84" height="58" rx="4" ry="4" fill="#2a2a2a"/>
    <!-- Bildschirm-Rand -->
    <rect x="10" y="3" width="80" height="55" rx="3" ry="3" fill="#1a1a1a"/>
    <!-- Bildschirm -->
    <rect x="12" y="5" width="76" height="50" rx="2" ry="2" fill="#e5e7eb"/>
    <!-- Notch mit Kamera -->
    <path d="M42,3 L58,3 L56,6 L44,6 Z" fill="#1a1a1a"/>
    <circle cx="50" cy="4" r="1" fill="#2a2a2a"/>
    <!-- Untere Basis (Tastatur-Bereich) -->
    <path d="M2,60 L98,60 L100,62 Q100,64 98,65 L2,65 Q0,64 0,62 Z" fill="#d4d4d4"/>
    <!-- Tastatur-Aussparung -->
    <rect x="10" y="61" width="80" height="3" rx="0.5" fill="#b0b0b0"/>
    <!-- Trackpad -->
    <rect x="35" y="66" width="30" height="18" rx="2" fill="#c8c8c8"/>
    <!-- Basis-Boden -->
    <path d="M0,64 L100,64 L98,98 Q97,100 95,100 L5,100 Q3,100 2,98 Z" fill="#c4c4c4"/>
    <!-- Lüftungsschlitze -->
    <rect x="15" y="85" width="70" height="0.5" fill="#a0a0a0"/>
    <rect x="15" y="87" width="70" height="0.5" fill="#a0a0a0"/>
  </svg>`
};

const macbookAir: MockupElement = {
  id: 'mockup-macbook-air',
  type: 'mockup',
  name: 'MacBook Air',
  tags: ['macbook', 'apple', 'laptop', 'air', 'thin', 'macos'],
  category: 'laptop',
  deviceType: 'laptop',
  screenArea: { x: 11, y: 4, width: 78, height: 48, borderRadius: 2 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Bildschirm-Deckel (dünner als Pro) -->
    <rect x="7" y="1" width="86" height="54" rx="3" ry="3" fill="#e8e8e8"/>
    <!-- Bildschirm-Rand -->
    <rect x="9" y="2" width="82" height="51" rx="2" ry="2" fill="#1a1a1a"/>
    <!-- Bildschirm -->
    <rect x="11" y="4" width="78" height="48" rx="2" ry="2" fill="#e5e7eb"/>
    <!-- Kamera -->
    <circle cx="50" cy="3" r="0.8" fill="#2a2a2a"/>
    <!-- Untere Basis (keilförmig) -->
    <path d="M5,55 L95,55 L97,56 Q98,58 97,60 L94,95 Q93,97 91,97 L9,97 Q7,97 6,95 L3,60 Q2,58 3,56 Z" fill="#d8d8d8"/>
    <!-- Tastatur-Bereich -->
    <rect x="12" y="58" width="76" height="25" rx="1" fill="#c0c0c0"/>
    <!-- Trackpad -->
    <rect x="32" y="85" width="36" height="10" rx="2" fill="#d0d0d0"/>
  </svg>`
};

// ============================================================================
// DESKTOP MOCKUPS
// ============================================================================

const desktopMonitor: MockupElement = {
  id: 'mockup-desktop-monitor',
  type: 'mockup',
  name: 'Desktop Monitor',
  tags: ['monitor', 'desktop', 'screen', 'display', 'pc'],
  category: 'desktop',
  deviceType: 'desktop',
  screenArea: { x: 5, y: 4, width: 90, height: 62, borderRadius: 2 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Monitor-Rahmen -->
    <rect x="2" y="1" width="96" height="70" rx="4" ry="4" fill="#2a2a2a"/>
    <!-- Bildschirm-Rand -->
    <rect x="4" y="3" width="92" height="65" rx="2" ry="2" fill="#1a1a1a"/>
    <!-- Bildschirm -->
    <rect x="5" y="4" width="90" height="62" rx="2" ry="2" fill="#e5e7eb"/>
    <!-- Unterer Rand mit Logo -->
    <rect x="2" y="66" width="96" height="5" fill="#2a2a2a"/>
    <circle cx="50" cy="68.5" r="1.5" fill="#3a3a3a"/>
    <!-- Standfuß-Hals -->
    <path d="M40,71 L60,71 L58,82 L42,82 Z" fill="#3a3a3a"/>
    <!-- Standfuß-Basis -->
    <ellipse cx="50" cy="88" rx="25" ry="8" fill="#3a3a3a"/>
    <ellipse cx="50" cy="86" rx="22" ry="6" fill="#4a4a4a"/>
  </svg>`
};

const imac: MockupElement = {
  id: 'mockup-imac',
  type: 'mockup',
  name: 'iMac',
  tags: ['imac', 'apple', 'desktop', 'all-in-one', 'macos'],
  category: 'desktop',
  deviceType: 'desktop',
  screenArea: { x: 6, y: 4, width: 88, height: 58, borderRadius: 2 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Monitor-Gehäuse -->
    <rect x="3" y="1" width="94" height="68" rx="5" ry="5" fill="#f5f5f5"/>
    <!-- Bildschirm-Bereich -->
    <rect x="5" y="3" width="90" height="62" rx="3" ry="3" fill="#1a1a1a"/>
    <!-- Bildschirm -->
    <rect x="6" y="4" width="88" height="58" rx="2" ry="2" fill="#e5e7eb"/>
    <!-- Kamera -->
    <circle cx="50" cy="2" r="0.8" fill="#2a2a2a"/>
    <!-- Kinn mit Apple Logo -->
    <rect x="3" y="62" width="94" height="7" fill="#f5f5f5"/>
    <text x="50" y="67" text-anchor="middle" font-size="4" fill="#ccc"></text>
    <!-- Standfuß -->
    <path d="M42,69 L58,69 L56,85 L44,85 Z" fill="#e0e0e0"/>
    <!-- Standfuß-Basis -->
    <ellipse cx="50" cy="92" rx="22" ry="6" fill="#e8e8e8"/>
    <ellipse cx="50" cy="90" rx="20" ry="5" fill="#f0f0f0"/>
  </svg>`
};

// ============================================================================
// WATCH MOCKUPS
// ============================================================================

const appleWatch: MockupElement = {
  id: 'mockup-apple-watch',
  type: 'mockup',
  name: 'Apple Watch',
  tags: ['apple', 'watch', 'smartwatch', 'wearable', 'fitness'],
  category: 'watch',
  deviceType: 'watch',
  screenArea: { x: 18, y: 22, width: 64, height: 56, borderRadius: 12 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Oberes Armband -->
    <path d="M25,0 L75,0 L75,18 Q50,22 25,18 Z" fill="#4a4a4a"/>
    <!-- Unteres Armband -->
    <path d="M25,100 L75,100 L75,82 Q50,78 25,82 Z" fill="#4a4a4a"/>
    <!-- Uhr-Gehäuse -->
    <rect x="12" y="16" width="76" height="68" rx="18" ry="18" fill="#2a2a2a"/>
    <!-- Inneres Gehäuse -->
    <rect x="15" y="19" width="70" height="62" rx="16" ry="16" fill="#1a1a1a"/>
    <!-- Bildschirm -->
    <rect x="18" y="22" width="64" height="56" rx="12" ry="12" fill="#e5e7eb"/>
    <!-- Digital Crown (rechts) -->
    <rect x="86" y="35" width="4" height="14" rx="2" fill="#3a3a3a"/>
    <!-- Seitentaste -->
    <rect x="86" y="52" width="3" height="8" rx="1" fill="#3a3a3a"/>
  </svg>`
};

// ============================================================================
// APPAREL/MERCHANDISE MOCKUPS
// ============================================================================

const tshirt: MockupElement = {
  id: 'mockup-tshirt',
  type: 'mockup',
  name: 'T-Shirt',
  tags: ['tshirt', 'shirt', 'clothing', 'apparel', 'merch', 'fashion'],
  category: 'apparel',
  deviceType: 'apparel',
  screenArea: { x: 30, y: 28, width: 40, height: 40, borderRadius: 0 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- T-Shirt Silhouette -->
    <path d="M30,8
      L20,10 L8,25 L18,30 L22,22
      L22,95 Q22,98 25,98
      L75,98 Q78,98 78,95
      L78,22 L82,30 L92,25 L80,10
      L70,8
      Q65,2 50,2 Q35,2 30,8 Z"
      fill="currentColor" stroke="none"/>
    <!-- Kragen -->
    <path d="M35,8 Q50,15 65,8 Q60,2 50,2 Q40,2 35,8 Z"
      fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="1"/>
    <!-- Ärmel-Nähte -->
    <path d="M22,22 L18,30" fill="none" stroke="rgba(0,0,0,0.08)" stroke-width="0.5"/>
    <path d="M78,22 L82,30" fill="none" stroke="rgba(0,0,0,0.08)" stroke-width="0.5"/>
    <!-- Druck-Bereich (Design geht hier) -->
    <rect x="30" y="28" width="40" height="40" fill="#e5e7eb" opacity="0.9"/>
  </svg>`
};

const mug: MockupElement = {
  id: 'mockup-mug',
  type: 'mockup',
  name: 'Tasse',
  tags: ['mug', 'cup', 'coffee', 'merchandise', 'print'],
  category: 'apparel',
  deviceType: 'apparel',
  screenArea: { x: 15, y: 25, width: 50, height: 50, borderRadius: 0 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <!-- Tassen-Gradient für 3D-Effekt -->
      <linearGradient id="mug-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stop-color="rgba(0,0,0,0.05)"/>
        <stop offset="50%" stop-color="rgba(255,255,255,0.1)"/>
        <stop offset="100%" stop-color="rgba(0,0,0,0.15)"/>
      </linearGradient>
    </defs>
    <!-- Schatten -->
    <ellipse cx="50" cy="95" rx="35" ry="4" fill="rgba(0,0,0,0.1)"/>
    <!-- Tassen-Körper -->
    <path d="M10,15
      L10,85 Q10,95 25,95
      L70,95 Q85,95 85,85
      L85,15 Z"
      fill="currentColor"/>
    <!-- 3D-Overlay -->
    <path d="M10,15 L10,85 Q10,95 25,95 L70,95 Q85,95 85,85 L85,15 Z"
      fill="url(#mug-gradient)"/>
    <!-- Oberer Rand -->
    <ellipse cx="47.5" cy="15" rx="37.5" ry="5" fill="currentColor"/>
    <ellipse cx="47.5" cy="15" rx="35" ry="4" fill="rgba(0,0,0,0.1)"/>
    <!-- Henkel -->
    <path d="M85,30
      Q105,30 105,55
      Q105,75 85,75"
      fill="none" stroke="currentColor" stroke-width="8" stroke-linecap="round"/>
    <!-- Druck-Bereich -->
    <rect x="15" y="25" width="50" height="50" fill="#e5e7eb" rx="2"/>
  </svg>`
};

const businessCard: MockupElement = {
  id: 'mockup-business-card',
  type: 'mockup',
  name: 'Visitenkarte',
  tags: ['business', 'card', 'corporate', 'print', 'stationery'],
  category: 'apparel',
  deviceType: 'apparel',
  screenArea: { x: 8, y: 18, width: 84, height: 64, borderRadius: 2 },
  perspective: { rotateX: 10, rotateY: -5 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="card-shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="2" dy="4" stdDeviation="3" flood-opacity="0.2"/>
      </filter>
    </defs>
    <!-- Schatten-Karte (leicht versetzt für 3D) -->
    <rect x="10" y="22" width="84" height="64" rx="2" fill="rgba(0,0,0,0.1)"/>
    <!-- Hauptkarte -->
    <rect x="6" y="16" width="88" height="68" rx="3" fill="#ffffff" filter="url(#card-shadow)"/>
    <!-- Kartenrand -->
    <rect x="6" y="16" width="88" height="68" rx="3" fill="none" stroke="#e5e7eb" stroke-width="0.5"/>
    <!-- Druck-Bereich -->
    <rect x="8" y="18" width="84" height="64" fill="#f9fafb" rx="2"/>
  </svg>`
};

const poster: MockupElement = {
  id: 'mockup-poster',
  type: 'mockup',
  name: 'Poster/Plakat',
  tags: ['poster', 'print', 'wall', 'art', 'frame'],
  category: 'apparel',
  deviceType: 'apparel',
  screenArea: { x: 12, y: 8, width: 76, height: 84, borderRadius: 0 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="poster-shadow" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="3" dy="5" stdDeviation="4" flood-opacity="0.25"/>
      </filter>
    </defs>
    <!-- Wand-Hintergrund -->
    <rect x="0" y="0" width="100" height="100" fill="#f0f0f0"/>
    <!-- Rahmen -->
    <rect x="8" y="5" width="84" height="90" rx="1" fill="#2a2a2a" filter="url(#poster-shadow)"/>
    <!-- Passepartout -->
    <rect x="10" y="7" width="80" height="86" fill="#ffffff"/>
    <!-- Poster-Bereich -->
    <rect x="12" y="8" width="76" height="84" fill="#e5e7eb"/>
  </svg>`
};

// ============================================================================
// EXPORT - Alle Mockups als Bibliothek
// ============================================================================

export const MOCKUP_LIBRARY: MockupElement[] = [
  // Phones
  iphone15Front,
  iphone15Angled,
  androidPhone,
  // Tablets
  ipadPro,
  ipadLandscape,
  // Laptops
  macbookPro,
  macbookAir,
  // Desktops
  desktopMonitor,
  imac,
  // Watches
  appleWatch,
  // Apparel/Merchandise
  tshirt,
  mug,
  businessCard,
  poster,
];

// Kategorien für UI-Gruppierung
export const MOCKUP_CATEGORIES = [
  { id: 'phone', label: 'Smartphones', description: 'iPhone, Android und mehr' },
  { id: 'tablet', label: 'Tablets', description: 'iPad und andere Tablets' },
  { id: 'laptop', label: 'Laptops', description: 'MacBook und Notebooks' },
  { id: 'desktop', label: 'Desktops', description: 'Monitore und All-in-Ones' },
  { id: 'watch', label: 'Smartwatches', description: 'Apple Watch und mehr' },
  { id: 'apparel', label: 'Merchandise', description: 'T-Shirts, Tassen, Print' },
] as const;

// Hilfsfunktion: Mockups nach Kategorie filtern
export function getMockupsByCategory(category: string): MockupElement[] {
  return MOCKUP_LIBRARY.filter(mockup => mockup.category === category);
}

// Hilfsfunktion: Mockups nach Device-Type filtern
export function getMockupsByDeviceType(deviceType: MockupElement['deviceType']): MockupElement[] {
  return MOCKUP_LIBRARY.filter(mockup => mockup.deviceType === deviceType);
}

// Hilfsfunktion: Mockups nach Tags durchsuchen
export function searchMockups(query: string): MockupElement[] {
  const lowerQuery = query.toLowerCase();
  return MOCKUP_LIBRARY.filter(mockup =>
    mockup.name.toLowerCase().includes(lowerQuery) ||
    mockup.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
}

// Hilfsfunktion: Mockup mit Perspektive transformieren
export function getMockupTransform(mockup: MockupElement): string {
  if (!mockup.perspective) return '';

  const { rotateX, rotateY } = mockup.perspective;
  return `perspective(500px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
}
